//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { InvisibleButton } from '@components/atoms/button/Button';
import {
  BodySecondaryText,
  LinkPrimaryText,
  SubtitleText,
} from '@components/atoms/text/Text';
import approveRightsSvg from '@images/preapproved_rights.svg';
import DateString from '@models/DateString';
import PartialCuid from '@models/PartialCuid';
import { getUserTimeZone } from '@redux/selectors/getUserTimeZone';
import { UIAFooter } from '@uia/layout/UIAFooter';
import { UIARightsMarkApprovedForm } from '@uia/rights/UIARightsMarkApprovedForm';
import { UIA_RIGHTS_FLOW_TRACKING_IDS } from '@uia/rights/constants/uiaRightsConstants';
import { getIsRenderedWithinAnotherWorkflow } from '@uia/rights/selectors/getUIARights';
import { isDefined } from '@utils/filterPredicates';

interface Props {
  expirationTime?: Date;
  onClose: () => void;
  onUpdateRights: () => void;
  partialCuid: PartialCuid;
}

export function UIARightsMarkedApproved({
  expirationTime,
  onClose,
  onUpdateRights,
  partialCuid,
}: Props) {
  // TODO: PD-269777 - Remove once UIA React port is complete
  const isRenderedWithinAnotherWorkflow = useSelector(
    getIsRenderedWithinAnotherWorkflow,
  );
  const [shouldShowForm, setShouldShowForm] = useState(false);
  const timezone = useSelector(getUserTimeZone);

  return shouldShowForm ? (
    <UIARightsMarkApprovedForm
      expiryTime={expirationTime}
      handleReturnToRightsOptions={() => setShouldShowForm(false)}
      handleSubmit={() => {
        setShouldShowForm(false);
      }}
      onUpdateRights={onUpdateRights}
      partialCuid={partialCuid}
    />
  ) : (
    <OverlayWrapper>
      <RightsApprovedContainer>
        <RightsApprovedImage alt='rights approved' src={approveRightsSvg} />
        <SubtitleText>Rights Approved</SubtitleText>
        <StyledText>
          You're free to add some products and distribute this content.
          {isDefined(expirationTime)
            ? ` Rights will expire on ${DateString.format(DateString.fromTimestamp(expirationTime, timezone), 'MM/DD/YY')}.`
            : ''}
        </StyledText>
        <UpsertExpiryDate
          onClick={() => setShouldShowForm(true)}
          trackingId={
            UIA_RIGHTS_FLOW_TRACKING_IDS.MARK_APPROVED_UPSERT_EXPIRY_DATE
          }
        >
          <LinkPrimaryText>
            {isDefined(expirationTime) ? 'Edit' : 'Add'} expiry date
          </LinkPrimaryText>
        </UpsertExpiryDate>
      </RightsApprovedContainer>
      {!isRenderedWithinAnotherWorkflow && (
        <UIAFooter>
          <UIAFooter.Actions>
            <UIAFooter.ActionButton
              onClick={() => onClose()}
              trackingId={UIA_RIGHTS_FLOW_TRACKING_IDS.SKIP_RIGHTS_DONE_BUTTON}
            >
              Done
            </UIAFooter.ActionButton>
          </UIAFooter.Actions>
        </UIAFooter>
      )}
    </OverlayWrapper>
  );
}

const OverlayWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  width: 100%;
`;

const UpsertExpiryDate = styled(InvisibleButton)``;

const RightsApprovedContainer = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: calc(${theme.spacings[1]} * 25); // 100px
  `,
);

const StyledText = styled(BodySecondaryText)(
  ({ theme }) => `
    text-align: center;
    padding: 0 ${theme.spacings[7]};
  `,
);

const RightsApprovedImage = styled.img`
  width: 100px;
`;
